import { faBars, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faHome } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { DarkThemeToggle } from 'components/dev/DarkThemeToggle';
import {
    CorporationAbbreviation,
    CorporationDropdown,
} from 'components/dropdown/CorporationDropdown';
import { LocationDropdown } from 'components/dropdown/LocationDropdown';
import { GlobalSearch } from 'components/global_search/GlobalSearch';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { CorporationLogoButton } from 'components/top_bar/CorporationLogoButton';
import { FavoritesPopup } from 'components/top_bar/FavoritesPopup';
import { UserPopup } from 'components/top_bar/UserPopup';
import { useScopeNavigation } from 'components/top_bar/useScopeNavigation';
import { usePermissions } from 'contexts/PermissionsContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { hasAccessToMultipleCorporations, useUser } from 'contexts/UserContext';
import {
    ShortcutDescription,
    useGlobalSearchVisibility,
} from 'custom_hooks/useGlobalSearchVisibility';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { memo, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { DebugToggle } from 'shared/debug/DebugToggle';
import styled from 'styled-components';
import * as theme from 'styles/theme';
import { MenuButton, MenuLink } from 'styles/theme_styles/button';
import { getCorporationLandingPageRoute } from 'utils/RouteHelper';

const TopBarWrapper = styled.nav`
    background: ${theme.navColor};
    color: ${theme.primaryText};
    border-bottom: 1px solid ${theme.borderColor};
`;

const TopBarSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
`;

const LocationDropdownLabel = styled.span`
    display: -webkit-box;
    // both of these webkit props have over 97% browser support.
    -webkit-box-orient: vertical;
    box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
`;

function TopBar() {
    const user = useUser();
    const { location, corporation, isLoading } = useRouteEntities();
    const { canViewGlobalSearchIcon } = usePermissions();
    const {
        isVisible: isGlobalSearchVisible,
        setIsVisible: setGlobalSearchVisible,
    } = useGlobalSearchVisibility();

    const routeLocation = useLocation();

    const {
        globalUrl,
        navigateToCorporationPage,
        navigateToLocationPage,
    } = useScopeNavigation(user.user);

    const [selectedCorporation, setSelectedCorporation] = useState<
        ICorporationResponse | undefined
    >(corporation);

    const { toggleDrawerExpanded } = useNavigationDrawerValues((state) => ({
        toggleDrawerExpanded: state.toggleDrawerExpanded,
    }));

    const isSmallScreen = useIsViewportAtMost(MediaSize.small);

    const entityNavigation = useMemo(
        () => (
            <>
                {selectedCorporation && (
                    <CorporationLogoButton
                        url={getCorporationLandingPageRoute(selectedCorporation.Id)}
                        selectedCorporation={selectedCorporation}
                    />
                )}
                <CorporationDropdown
                    value={selectedCorporation}
                    onChange={navigateToCorporationPage}
                    placeholder="Corporations"
                    className="bold flex-column align-center justify-center"
                    popupWidth="40rem"
                    renderLabel={isSmallScreen ? CorporationAbbreviation : undefined}
                />
                {selectedCorporation && (
                    <SpinnerArea isLoading={isLoading} hideChildren size="1x" noContainer>
                        <LocationDropdown
                            showFavoriteIcon
                            corporationId={selectedCorporation.Id}
                            value={location}
                            onChange={navigateToLocationPage}
                            placeholder="Locations"
                            className="bold flex-column align-center justify-center"
                            popupWidth="40rem"
                            renderLabel={({ value: loc }) => (
                                <LocationDropdownLabel className="ellipses-1-line">
                                    {loc.Name}
                                </LocationDropdownLabel>
                            )}
                        />
                    </SpinnerArea>
                )}
            </>
        ),
        [
            isLoading,
            isSmallScreen,
            location,
            navigateToCorporationPage,
            navigateToLocationPage,
            selectedCorporation,
        ]
    );

    useEffect(() => {
        setSelectedCorporation(corporation);
    }, [corporation]);

    if (
        /dashboards\/(deployment-and-value|desk-activity-overview|value-by-location)/.test(
            routeLocation.pathname
        )
    ) {
        return <></>;
    }

    return (
        <div>
            <GlobalSearch
                isVisible={isGlobalSearchVisible}
                onHide={() => setGlobalSearchVisible(false)}
            />
            <TopBarWrapper className="top-bar" aria-label="Primary navigation">
                <TopBarSection>
                    {isSmallScreen && (
                        <HoverIconButton
                            size="1.8rem"
                            aria-label="Expand"
                            title="Expand"
                            icon={faBars}
                            hoverIcon={faBars}
                            color="currentColor"
                            onClick={toggleDrawerExpanded}
                        />
                    )}
                    {hasAccessToMultipleCorporations(user.user) && (
                        <MenuLink
                            tabIndex={0}
                            $padding="1rem"
                            title="Go to global page"
                            aria-label="go to global page"
                            to={globalUrl}
                        >
                            <FontAwesomeIcon
                                className="f-18 cursor-pointer"
                                icon={faHome}
                            />
                        </MenuLink>
                    )}
                    {!isSmallScreen && entityNavigation}
                </TopBarSection>
                <TopBarSection>
                    {canViewGlobalSearchIcon && (
                        <MenuButton
                            aria-label="show or hide global search modal"
                            onClick={() => setGlobalSearchVisible(true)}
                            title={`Open Search (${ShortcutDescription})`}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                        </MenuButton>
                    )}
                    <FavoritesPopup
                        corporationId={selectedCorporation?.Id}
                        locationId={location?.Id}
                    />
                    <UserPopup />
                    <DarkThemeToggle />
                    <DebugToggle />
                </TopBarSection>
            </TopBarWrapper>
            {isSmallScreen && (
                <TopBarWrapper className="flex-row secondary-top-bar align-center">
                    {entityNavigation}
                </TopBarWrapper>
            )}
        </div>
    );
}

const memoTopBar = memo(TopBar);
export { memoTopBar as TopBar };
